import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";

type UserRole = "admin" | "content_manager" | null;

interface AuthContextType {
  user: User | null;
  session: Session | null;
  isAdmin: boolean;
  isContentManager: boolean;
  hasAdminAccess: boolean; // Either admin or content_manager
  userRole: UserRole;
  isLoading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signUp: (email: string, password: string, displayName?: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [userRole, setUserRole] = useState<UserRole>(null);
  const [isLoading, setIsLoading] = useState(true);

  const checkUserRole = async (userId: string): Promise<UserRole> => {
    try {
      // IMPORTANT: Do not read roles directly from `user_roles` here.
      // The roles table is admin-protected by RLS, and content managers cannot SELECT it.
      // Instead, use SECURITY DEFINER database functions to safely check access.
      const [isAdminRes, isContentManagerRes] = await Promise.all([
        supabase.rpc("is_admin", { _user_id: userId }),
        supabase.rpc("is_content_manager", { _user_id: userId }),
      ]);

      if (isAdminRes.error) throw isAdminRes.error;
      if (isContentManagerRes.error) throw isContentManagerRes.error;

      if (isAdminRes.data) return "admin";
      if (isContentManagerRes.data) return "content_manager";
      return null;
    } catch (err) {
      console.error("Error checking user role:", err);
      return null;
    }
  };

  useEffect(() => {
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        
        // Defer role check to avoid deadlock
        if (session?.user) {
          setTimeout(() => {
            checkUserRole(session.user.id).then(setUserRole);
          }, 0);
        } else {
          setUserRole(null);
        }
        
        setIsLoading(false);
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      
      if (session?.user) {
        checkUserRole(session.user.id).then((role) => {
          setUserRole(role);
          setIsLoading(false);
        });
      } else {
        setIsLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    
    // Log successful sign-in
    if (!error && data.user) {
      try {
        const profile = await supabase
          .from("admin_profiles")
          .select("display_name")
          .eq("user_id", data.user.id)
          .maybeSingle();

        await supabase.from("sign_in_logs").insert({
          user_id: data.user.id,
          email: data.user.email,
          display_name: profile.data?.display_name || null,
          user_agent: navigator.userAgent,
        });
      } catch (logError) {
        console.error("Failed to log sign-in:", logError);
      }
    }
    
    return { error: error as Error | null };
  };

  const signUp = async (email: string, password: string, displayName?: string) => {
    const redirectUrl = `${window.location.origin}/`;
    
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: {
          display_name: displayName,
        },
      },
    });
    return { error: error as Error | null };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setUserRole(null);
  };

  const isAdmin = userRole === "admin";
  const isContentManager = userRole === "content_manager";
  const hasAdminAccess = isAdmin || isContentManager;

  return (
    <AuthContext.Provider value={{ 
      user, 
      session, 
      isAdmin, 
      isContentManager,
      hasAdminAccess,
      userRole,
      isLoading, 
      signIn, 
      signUp, 
      signOut 
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
